import { useMemo } from 'react';

import { FontPack, ThemePack } from '@/types/theme-packs.types';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import { ICONS } from '@ui/icons/types';
import Icon from '@ui/icons/icon';
import { useAppSelector } from '@/store/hooks';
import { selectThemePacks } from '@/store/slices/templates/selectors';
import Button from '@ui/buttons/button/button';

type TThemePackFontVariantChangeProps = {
  themePack?: ThemePack;
  selectTab: () => void;
  active: boolean;
  disabled?: boolean;
};

const ThemePackFontVariantButton = ({
  themePack,
  selectTab,
  active,
  disabled = false,
}: TThemePackFontVariantChangeProps) => {
  const themePacks = useAppSelector(selectThemePacks);
  const workingFont = themePack?.font_pack;

  const fontPacks = useMemo(() => {
    if (!themePacks) return null;

    // get unique font packs from themePacks
    const retrieved: Map<number, FontPack> = new Map();
    themePacks.forEach((pack) => {
      if (pack.font_pack) {
        const fontPack = pack.font_pack;
        if (!retrieved.has(fontPack.font_pack)) {
          retrieved.set(fontPack.font_pack, fontPack);
        }
      }
    });

    // convert map to array
    return Array.from(retrieved.values());
  }, [themePacks]);

  if (!themePack || !fontPacks) return null;
  return (
    <>
      <Container
        background={active ? 'var(--gray-100)' : 'var(--gray-700)'}
        borderRadius="100px"
        height="32px"
        padding="2px"
        className="flex ai-center jc-space-between"
        style={{
          transition: 'all 0.2s ease-in-out',
          background: active ? 'var(--gray-100)' : undefined,
        }}
      >
        <Button
          clear
          backgroundColor={active ? 'var(--gray-900)' : 'var(--gray-700)'}
          hoverBackgroundColor="var(--gray-800)"
          borderRadius="100px"
          height={'28px'}
          className="flex ai-center gap-6 width-100-percent jc-space-between padding-l-8 padding-r-8 overflow-hidden"
          disabled={disabled}
          disabledOpacity={1}
          onClick={selectTab}
        >
          <Container className="flex ai-center gap-6">
            <Icon name={ICONS.FONT_AG_FILLED} size={12} color="var(--gray-300)" />
            <Text color="var(--gray-300)" className="C1" whiteSpace="nowrap">
              Fonts
            </Text>
          </Container>
          <Container background={'transparent'} className="radius-8 padding-h-2 flex ai-center max-width-250">
            <Text
              clamp={1}
              color="var(--gray-300)"
              fontFamily={workingFont?.heading_name}
              className="C1 flex max-width-115 text-ellipsis"
              whiteSpace="nowrap"
            >
              {workingFont?.heading_name?.replace(/__custom.*/, '')}
            </Text>
            <Text color="var(--gray-300)" className="C1 flex width-8">
              ,&nbsp;
            </Text>
            <Text
              clamp={1}
              color="var(--gray-300)"
              fontFamily={workingFont?.body_name}
              className="C1 flex max-width-115 text-ellipsis"
              whiteSpace="nowrap"
            >
              {workingFont?.body_name?.replace(/__custom.*/, '')}
            </Text>
          </Container>
        </Button>
      </Container>
    </>
  );
};

export default ThemePackFontVariantButton;
